import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import UserSettingsService from "src/services/userSettingsService";
import { useDispatch, useSelector } from "src/store";

import { updateSearchQuery } from "@dashboard/devices/store";
import type { Group } from "@dashboard/devices/types";
import { setColumnVisibility } from "@dashboard/devices/utils/columnVisibility";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { getGroupsForAggregation } from "@dashboard/groups/utils";
import { getBarValue } from "@dashboard/shared/limits/utils";
import { useCurrentWorkspace, useWorkspaceFeature } from "@dashboard/workspaces/api/helpers";
import { Theme } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ElasticService from "@services/elasticService";
import { CustomTileText } from "@shared/CustomTile";
import { ProgressBar } from "@shared/ProgressBar";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";
import { TextLink } from "@shared/TextLink";

import { useStatusCardStyles } from "../StatusCard.styles";

export const getBarColor = (barValue: number, limit: number, theme: Theme) => {
    if (limit === Infinity) {
        return theme.palette.emteria.success;
    }
    if (barValue >= 98) {
        return theme.palette.emteria.error;
    }
    if (barValue > 80) {
        return theme.palette.emteria.warning;
    }
    if (barValue > 0) {
        return theme.palette.emteria.success;
    }
    return theme.palette.emteria.success;
};

const EnrollmentProgressCard = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { t } = useTranslation(["pages", "common"]);
    const { classes: statusCardClasses } = useStatusCardStyles();
    const theme = useTheme();

    const { data: workspace } = useCurrentWorkspace();
    const groups: Group[] = useSelector(selectGroups);
    const deviceCount = useMemo(() => {
        return getGroupsForAggregation([], groups).reduce((acc, group) => acc + (group.statistics?.deviceCount ?? 0), 0);
    }, [groups]);
    const licenseLimit = useWorkspaceFeature("LicenseLimit");

    const barValue = getBarValue(deviceCount, licenseLimit);
    const barColor = getBarColor(barValue, licenseLimit, theme);

    const handleOnClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (deviceCount === licenseLimit && licenseLimit !== Infinity) {
            window.open("https://emteria.com/p/subscription", "_blank", "noopener,noreferrer");
        } else {
            const filters = [{ id: "licenseStatus", value: ["Used"] }];

            UserSettingsService.setWorkspaceValue("devicePage.columnFilters", filters);
            setColumnVisibility("licenseStatus");
            dispatch(updateSearchQuery(""));
            void navigate("/dashboard/devices");
        }
        const text = e.currentTarget.textContent;
        if (text) {
            ElasticService.createTransaction("overviewPageLinkClick", {
                tab: "fleet-size",
                text,
            });
        }
    };

    return (
        <SectionPanel title={t("pages:overviewPage.cards.enrollmentProgress.title")}>
            {!deviceCount ? <CustomTileText>{t("common:table.noDevices")}</CustomTileText> : null}
            {deviceCount && licenseLimit === Infinity ? (
                <TextLink onClick={handleOnClick} tooltip={t("pages:overviewPage.cards.enrollmentProgress.tooltip")}>
                    {t("pages:overviewPage.cards.enrollmentProgress.devicesEnrolled", { count: deviceCount })}
                </TextLink>
            ) : null}
            {deviceCount && licenseLimit !== Infinity ? (
                deviceCount === licenseLimit && workspace ? (
                    <TextLink onClick={handleOnClick} tooltip={t("pages:overviewPage.cards.enrollmentProgress.upgradeTooltip")}>
                        {t("pages:overviewPage.cards.enrollmentProgress.limitReached")}
                    </TextLink>
                ) : (
                    <TextLink onClick={handleOnClick} tooltip={t("pages:overviewPage.cards.enrollmentProgress.tooltip")}>
                        {t("pages:overviewPage.cards.enrollmentProgress.devicesEnrolledOf", {
                            total: deviceCount,
                            limit: licenseLimit,
                        })}
                    </TextLink>
                )
            ) : null}
            <ProgressBar
                className={statusCardClasses.progressBar}
                progress={deviceCount ? barValue : 0}
                color={barColor}
                hideLabel={!deviceCount}
            />
        </SectionPanel>
    );
};

export default EnrollmentProgressCard;
